/**
 * PORTED — RO-11-05 / #44590.
 * Origin: `excellent/app/_chrome/rail-tree-nav.tsx`.
 *
 * Changes: next/link → @excellent/ui/router (RouterLink). Copied into
 * client/shell/chrome/ — never import from app/ (PRD D6).
 */

import { RouterLink as Link } from "@excellent/ui/router";
import { type ReactNode } from "react";
import { cn } from "@excellent/ui";
import { RailMarkerNav, type RailMarkerNavItem } from "./rail-marker-nav";

/**
 * RailTreeNav — the right-gutter mirror of the left `ModeRail`. A vertical list
 * of top-level GROUP rows; the ACTIVE group reveals its `items` BENEATH it in an
 * animated accordion (the exact `ModeSubNav` grammar: a single block that
 * expands height + opacity together, so switching groups collapses the outgoing
 * one while the incoming one expands at the SAME TIME and the list barely
 * shifts). The nested list carries the faint guide hairline + the single sliding
 * marker via `RailMarkerNav`, so both sides of the app share one design.
 *
 * Pure presentation: the caller (ContextRail) resolves which group is `active`
 * (open) and each nested item's `active` (route match for a multi-page group,
 * scroll-spy for a consolidated one). Every group's accordion stays mounted —
 * collapsed when inactive — so the open/close animates both directions.
 */

export interface RailTreeNavGroup {
  key: string;
  label: string;
  /** Group landing page (the row links here). */
  href: string;
  /** Optional pre-rendered icon JSX, sized to 14px. */
  icon?: ReactNode;
  /** This group is the active one → its accordion is open. */
  active: boolean;
  /** Nested entries, with `.active` already resolved by the caller. */
  items: RailMarkerNavItem[];
}

export function RailTreeNav({
  groups,
  label,
}: {
  groups: RailTreeNavGroup[];
  /** Small uppercase heading above the tree (e.g. "Settings"). */
  label?: string;
}) {
  // A plain <div>, not a <nav>: the rail's single navigation landmark is the
  // <nav aria-label> ContextRail already wraps this in — a nested <nav> would
  // announce a duplicate "Settings" region.
  return (
    <div className="flex flex-col gap-0.5">
      {label ? (
        <span className="px-2.5 pb-1 type-subtext font-medium uppercase tracking-[0.08em] text-faint">
          {label}
        </span>
      ) : null}
      {groups.map((g) => (
        <RailTreeRow key={g.key} group={g} />
      ))}
    </div>
  );
}

/** One group row + its nested (animated) sub-nav. Mirrors `ModeRow`/`ModeSubNav`
 *  in mode-rail.tsx — the row reads as the primary affordance (foreground +
 *  medium when active, muted at rest), the nested items quieter beneath it. */
function RailTreeRow({ group }: { group: RailTreeNavGroup }) {
  const open = group.active;
  return (
    <div>
      <Link
        href={group.href}
        aria-current={open ? "page" : undefined}
        className={cn(
          "group flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 type-body tracking-tight outline-none transition-colors",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
          open
            ? "font-medium text-foreground"
            : "text-faint hover:text-foreground",
        )}
      >
        {/* No icons in the nav — EVER (Matt, 2026-07-23). Label only. */}
        <span className="truncate">{group.label}</span>
      </Link>
      {group.items.length > 0 ? (
        <RailTreeAccordion open={open} items={group.items} />
      ) : null}
    </div>
  );
}

/**
 * The nested sub-nav, nested + animated — one accordion: grid-rows (0fr↔1fr) +
 * opacity transition together, driven by `open`. `@starting-style` (the
 * `starting:` utils) animates a panel that mounts already-open (first visit)
 * from collapsed. Kept mounted when closed (with `inert`) so it can animate shut
 * and its links stay out of the tab order while hidden. This is the same block
 * accordion the left ModeRail uses; the marker + hairline come from RailMarkerNav.
 */
function RailTreeAccordion({
  open,
  items,
}: {
  open: boolean;
  items: RailMarkerNavItem[];
}) {
  return (
    <div
      className={cn(
        "grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:!transition-none",
        open
          ? "grid-rows-[1fr] opacity-100 starting:grid-rows-[0fr] starting:opacity-0"
          : "grid-rows-[0fr] opacity-0",
      )}
    >
      <div className="overflow-hidden" inert={!open}>
        <div className="mt-0.5 pb-0.5">
          <RailMarkerNav items={items} />
        </div>
      </div>
    </div>
  );
}
